import { Static, Type } from '@sinclair/typebox';

/**
 * Brouillon du quiz d'avant-inscription (`/start`, écrans 1 à 6 de la spec « Onboarding
 * Propelsia » v4) — colonne `lead.quiz` (JSON), validée à CHAQUE écriture et à chaque lecture
 * (`Value.Clean` + `Value.Check`, comme `lead.qualification`).
 *
 * Invariants :
 * - AUCUNE donnée personnelle ici (ni nom, ni téléphone, ni e-mail, ni adresse) : le brouillon
 *   est créé avant l'écran 7 et relu par son token côté LP. Les coordonnées vivent sur les
 *   colonnes `lead.email` / `lead.phone_e164` quand elles arrivent (écran 2 bis ou 7) ;
 * - chaque écran écrit son champ et rien d'autre (reprise au champ exact) ;
 * - `v` = version du contrat, incrémentée quand une clé change de sens.
 */
export const LEAD_QUIZ_VERSION = 1;

/** Verticale de la pub (`/start?v=`) ou carte choisie à l'écran 1. */
export const QuizVerticaleSchema = Type.Union([
  Type.Literal('beaute'),
  Type.Literal('coiffure'),
  Type.Literal('bien_etre'),
  Type.Literal('coach'),
  Type.Literal('autre'),
]);
export type QuizVerticale = Static<typeof QuizVerticaleSchema>;

/** Écran 2 — maturité. `projet` bascule le lead en `LeadState.DISTANT_PROJECT`. */
export const QuizSituationSchema = Type.Union([
  Type.Literal('deja_lancee'),
  Type.Literal('je_me_lance'),
  Type.Literal('projet'),
]);
export type QuizSituation = Static<typeof QuizSituationSchema>;

/** Écran 3 — besoins, dans l'ordre coché (le premier = besoin principal). */
export const QuizBesoinSchema = Type.Union([
  Type.Literal('trouver_clientes'),
  Type.Literal('etre_visible'),
  Type.Literal('m_organiser'),
  Type.Literal('etre_payee'),
  Type.Literal('gagner_du_temps'),
]);
export type QuizBesoin = Static<typeof QuizBesoinSchema>;

/** Écran 4 — comment les rendez-vous se prennent aujourd'hui. */
export const QuizRdvAujourdhuiSchema = Type.Union([
  Type.Literal('telephone'),
  Type.Literal('outil'),
  Type.Literal('rien'),
]);

/** Écran 6 — où le pro exerce (plusieurs possibles). Pré-remplit l'étape 2 du tunnel. */
export const QuizLieuSchema = Type.Union([
  Type.Literal('sur_place'),
  Type.Literal('deplacement'),
  Type.Literal('visio'),
]);
export type QuizLieu = Static<typeof QuizLieuSchema>;

/**
 * Écran 6 — chemin choisi pour décrire l'activité. `catalogue` = métier choisi dans le
 * référentiel puis prestations cochées dans la liste proposée (parcours par défaut depuis le
 * 27/10/2026) ; les autres valeurs restent lisibles pour les brouillons antérieurs.
 */
export const QuizSourceOffreSchema = Type.Union([
  Type.Literal('catalogue'),
  Type.Literal('photo'),
  Type.Literal('pulse'),
  Type.Literal('texte'),
  Type.Literal('lien'),
]);

/**
 * Écran 5 — ce qui existe déjà (oui / pas encore). `rien` = « rien pour l'instant, je démarre » :
 * un choix explicite, exclusif des autres, pour que celui qui part de zéro ne croie pas qu'il lui
 * manque quelque chose (retour Alexandre, 29/10). Continuer sans rien cocher reste possible.
 */
export const QuizVisibiliteSchema = Type.Object({
  instagram: Type.Optional(Type.Boolean()),
  google: Type.Optional(Type.Boolean()),
  site: Type.Optional(Type.Boolean()),
  bouche_a_oreille: Type.Optional(Type.Boolean()),
  rien: Type.Optional(Type.Boolean()),
});

/** Prestation extraite du flyer / de Pulse / du texte libre, avec un indice de confiance par champ. */
export const QuizOffrePrestationSchema = Type.Object({
  nom: Type.String({ maxLength: 120 }),
  prix_eur: Type.Optional(Type.Number({ minimum: 0 })),
  duree_min: Type.Optional(Type.Integer({ minimum: 5 })),
  /** `prix_eur` proposé par le modèle à l'écran 6 (métier précis), pas saisi ni lu chez le pro : « à confirmer ». */
  prix_estime: Type.Optional(Type.Boolean()),
  /** 0..1 — ce que Pulse a lu avec certitude vs ce qu'il a deviné. */
  confiance: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
});

/** Écran 6 — brouillon d'offre. Nourrit `LeadPreviewFromScratchBriefSchema` (lot 3). */
export const QuizOffreBrouillonSchema = Type.Object({
  nom_activite: Type.Optional(Type.String({ maxLength: 120 })),
  ville: Type.Optional(Type.String({ maxLength: 120 })),
  prestations: Type.Optional(Type.Array(QuizOffrePrestationSchema, { maxItems: 30 })),
  /** Clé S3 brute du logo extrait (jamais une URL signée). */
  logo_key: Type.Optional(Type.String({ maxLength: 512 })),
  /** Couleurs dominantes du flyer, hex, 3 max — alimentent l'étape 4 (site). */
  couleurs: Type.Optional(Type.Array(Type.String({ pattern: '^#[0-9a-fA-F]{6}$' }), { maxItems: 3 })),
  /** Une phrase « ce qui vous distingue », avec les mots du pro (≤ 200). */
  phrase_distinctive: Type.Optional(Type.String({ maxLength: 200 })),
  ton: Type.Optional(Type.Union([Type.Literal('chaleureux'), Type.Literal('chic'), Type.Literal('direct')])),
  /** Texte libre de l'écran 6, tronqué : matière pour Pulse, jamais affiché tel quel. */
  texte_libre: Type.Optional(Type.String({ maxLength: 1000 })),
});

export const LeadQuizSchema = Type.Object({
  v: Type.Literal(LEAD_QUIZ_VERSION),
  /** Verticale de la pub (`?v=`), telle que reçue — pas la carte choisie. */
  verticale_pub: Type.Optional(QuizVerticaleSchema),
  /** Écran 1 (carte « autre ») ou écran 6 : le métier avec les mots du pro, jamais réécrit. */
  metier: Type.Optional(Type.String({ maxLength: 80 })),
  /** Écran 6 : métier du référentiel (`activity.slug`) choisi ou rattaché au métier saisi. */
  activity_slug: Type.Optional(Type.String({ maxLength: 120 })),
  verticale: Type.Optional(QuizVerticaleSchema),
  /** Confiance du classement « autre métier » → verticale (0..1). */
  verticale_confiance: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  /** Écran 2. */
  situation: Type.Optional(QuizSituationSchema),
  /** Écran 3. */
  besoins: Type.Optional(Type.Array(QuizBesoinSchema, { maxItems: 5 })),
  /** Écran 4. */
  rdv_aujourdhui: Type.Optional(QuizRdvAujourdhuiSchema),
  outil_url: Type.Optional(Type.String({ maxLength: 2048 })),
  outil_nom: Type.Optional(Type.String({ maxLength: 64 })),
  /** Écran 5. */
  visibilite: Type.Optional(QuizVisibiliteSchema),
  instagram_url: Type.Optional(Type.String({ maxLength: 2048 })),
  site_url: Type.Optional(Type.String({ maxLength: 2048 })),
  /** Écran 6. */
  lieux: Type.Optional(Type.Array(QuizLieuSchema, { maxItems: 3 })),
  source_offre: Type.Optional(QuizSourceOffreSchema),
  offre_brouillon: Type.Optional(QuizOffreBrouillonSchema),
  /** Dernier écran atteint (1..7, « 2bis » = 2) — reprise au champ exact. */
  derniere_etape: Type.Optional(Type.Integer({ minimum: 1, maximum: 7 })),
  /** Contexte technique non personnel (A/B, device), dupliqué sur le pro à l'inscription. */
  device: Type.Optional(Type.Union([Type.Literal('mobile'), Type.Literal('desktop')])),
  langue: Type.Optional(Type.String({ maxLength: 8 })),
});
export type LeadQuizObject = Static<typeof LeadQuizSchema>;

/** Premier besoin coché = besoin principal (écran 3, règle « l'ordre compte »). */
export const quizBesoinPrincipal = (quiz: Pick<LeadQuizObject, 'besoins'> | null | undefined): QuizBesoin | null =>
  quiz?.besoins?.[0] ?? null;
